import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { formatLocalDate, parseLocalDate, isCompleteDateString } from '../utils/dateOnly.mjs';

/**
 * Date field that can actually be typed into.
 *
 * A plain controlled `<input type="date">` fights the user: the browser reports
 * an EMPTY value until all three segments are filled, so a parent that mirrors
 * `value` straight back wipes the day and month as soon as you reach the year.
 *
 * This keeps the in-progress text in local state and only tells the parent once
 * a genuine date exists, so day → month → year typing works. The calendar picker
 * is unaffected: it produces a complete date, which commits immediately.
 *
 * @param {Date|string|null} value    current date (from parent state)
 * @param {(d: Date|null) => void} onChange  called only with a real date, or null when cleared
 */
const DateInput = ({ value, onChange, className, readOnly, min, max, ...rest }) => {
  const [text, setText] = useState(() => formatLocalDate(value));
  const focused = useRef(false);
  // Whether the user changed the field during this focus. The parent can set a
  // date while the box has focus (an invoice read landing), which the box does
  // not show until blur - an empty box they never touched is not a clear.
  const dirty = useRef(false);

  // Mirror the parent while the user isn't editing. Skipping this during focus
  // is what stops the field being reset mid-entry.
  useEffect(() => {
    if (!focused.current) setText(formatLocalDate(value));
  }, [value]);

  const handleChange = (e) => {
    const next = e.target.value;
    dirty.current = true;
    setText(next);                     // always keep what the user typed
    if (isCompleteDateString(next)) {
      onChange(parseLocalDate(next));  // only surface real dates upward
    }
  };

  const handleBlur = () => {
    focused.current = false;
    if (!text && dirty.current) {
      onChange(null);                  // genuinely cleared
    } else if (!dirty.current || !isCompleteDateString(text)) {
      setText(formatLocalDate(value)); // untouched, or abandoned half-entry - show the parent's date
    }
  };

  return (
    <input
      type="date"
      value={text}
      onChange={handleChange}
      onFocus={() => { focused.current = true; dirty.current = false; }}
      onBlur={handleBlur}
      className={className}
      readOnly={readOnly}
      min={min}
      max={max}
      {...rest}
    />
  );
};

DateInput.propTypes = {
  value: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  onChange: PropTypes.func.isRequired,
  className: PropTypes.string,
  readOnly: PropTypes.bool,
  min: PropTypes.string,
  max: PropTypes.string,
};

export default DateInput;
